import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Save, ShieldCheck, History, SlidersHorizontal, Send } from 'lucide-react';
import { capacityLimitsService, EnforcementPanel, LimitCheck } from 'api/services/capacityLimits.service';
import { byAttention, statusLabel, statusTone, NOT_YET_ENFORCED_COPY } from '../capacityEnforcementRules';
import CapacityHistoryModal from './CapacityHistoryModal';
import TrunkCapacityModal from './TrunkCapacityModal';
import PushToSwitchModal from './PushToSwitchModal';
import SwitchRuntimeSection from './SwitchRuntimeSection';

const TH = 'px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider';
const TD = 'px-3 py-2 text-sm text-gray-700 dark:text-gray-300 align-top';
const inf = (v: number | null | undefined) => (v === null || v === undefined ? '∞' : v);

/**
 * "Enforced on switch" (design phase 0). Master / Green Giraffe only. Reads the switches;
 * changes nothing on them. Trunk capacity can be increased / decreased here with a reason.
 */
const CapacityEnforcementPanel: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
  const [panel, setPanel] = useState<EnforcementPanel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [history, setHistory] = useState<{ scope: LimitCheck['scope']; value: string; title: string } | null>(null);
  const [trunk, setTrunk] = useState<LimitCheck | null>(null);
  const [pushing, setPushing] = useState<LimitCheck | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const res = await capacityLimitsService.enforcement();
    if (res.success) setPanel(res.data || null); else setError(res.error || 'Failed to check the switches');
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const switchNames = useMemo(() => {
    const m: Record<string, string> = {};
    panel?.switches.forEach(s => { m[String(s.providerId)] = s.name; });
    return m;
  }, [panel]);

  const record = async () => {
    const res = await capacityLimitsService.recordEnforcement();
    setNote(res.success ? `Recorded ${res.data?.recorded ?? 0} verdict(s).` : (res.error || 'Record failed'));
  };

  const title = (l: LimitCheck) => (l.scope === 'PROVIDER' && switchNames[l.scopeValue]
    ? `${switchNames[l.scopeValue]} (#${l.scopeValue})` : `${l.scope} ${l.scopeValue}`);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" /> Enforced on switch
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Read from each switch's rate_limit_config, dialing info and enforcement status. Only "Push to switch" writes to a switch.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="px-3 py-1.5 bg-gray-800 text-white rounded-lg text-sm inline-flex items-center gap-1 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Re-check
          </button>
          <button onClick={record} className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm inline-flex items-center gap-1 text-gray-700 dark:text-gray-200">
            <Save className="w-4 h-4" /> Record check
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>}
      {note && <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded-lg px-3 py-2">{note}</div>}
      {!panel && loading && <p className="text-sm text-gray-500">Reading the switches…</p>}

      {panel && (
        <>
          <div data-testid="cpass-enforce-banner"
            className={`text-xs rounded-lg px-3 py-2 border ${panel.cpassEnforceLimits ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
            {panel.cpassEnforceLimits
              ? 'cpass ENFORCES number and campaign limits on calls it places (cpass.capacity.enforce-cpass-limits=true): a call over the limit is refused.'
              : 'cpass number and campaign limits are in SHADOW (cpass.capacity.enforce-cpass-limits=false): a call over the limit is logged as "would refuse" and still placed.'}
          </div>
          <ul className="text-xs text-gray-600 dark:text-gray-400 list-disc ml-5 space-y-0.5">
            {panel.notes.map(n => <li key={n}>{n}</li>)}
          </ul>

          <div className="flex flex-wrap gap-2">
            {panel.switches.map(s => (
              <span key={s.providerId} className={`text-xs px-2 py-1 rounded ${s.readable && !s.error ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}
                title={s.error || ''}>
                {s.name}: {s.readable ? `${s.dbRows} limiter row(s), ${s.numbers} DIDs` : 'unreadable'}{s.error ? ` — ${s.error}` : ''}
              </span>
            ))}
            {Object.entries(panel.counts).map(([k, v]) => (
              <span key={k} className={`text-xs px-2 py-1 rounded-full ${statusTone(k as LimitCheck['status'])}`}>
                {statusLabel[k as LimitCheck['status']] || k}: {v}
              </span>
            ))}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>{['Limit', 'cpass', 'Switch key(s)', 'Switch', 'Status', ''].map(h => <th key={h} className={TH}>{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {panel.limits.length === 0 && <tr><td colSpan={6} className={`${TD} text-center text-gray-500`}>No limits in cpass.</td></tr>}
                {byAttention(panel.limits).map(l => (
                  <tr key={l.limitId}>
                    <td className={TD}>
                      <div className="font-mono">{title(l)}</div>
                      <div className="text-xs text-gray-400">{l.source || 'MANUAL'}{l.enabled ? '' : ' · disabled'}</div>
                      {l.placeholder && <div className="text-xs font-medium text-red-700">placeholder — to be removed</div>}
                    </td>
                    <td className={TD}>{inf(l.cps)} CPS / {inf(l.maxChannels)} ch</td>
                    <td className={TD}>
                      {l.switches.flatMap(s => s.dimensions.slice(0, 3).map(d => (
                        <div key={`${s.switchProviderId}-${d.type}-${d.value}`} className="font-mono text-xs">
                          {s.switchName ? `${s.switchName}: ` : ''}{d.type}={d.value}
                        </div>
                      )))}
                      {l.switches.some(s => s.dimensions.length > 3) && <div className="text-xs text-gray-400">+ other spellings</div>}
                      {l.switches.every(s => s.dimensions.length === 0) && <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className={TD}>
                      {l.switches.filter(s => s.matchedRows.length > 0).map(s => (
                        <div key={String(s.switchProviderId)} className="text-xs">{s.switchName}: {inf(s.switchCps)} CPS / {inf(s.switchConcurrency)} ch</div>
                      ))}
                      {l.switches.every(s => s.matchedRows.length === 0) && <span className="text-xs text-gray-400">no row</span>}
                    </td>
                    <td className={TD}>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusTone(l.status)}`}>{statusLabel[l.status]}</span>
                      {l.reason && <div className="text-xs text-gray-500 mt-1 max-w-md">{l.reason}</div>}
                      {l.caveats.length > 0 && (
                        <details className="text-xs text-gray-500 mt-1">
                          <summary className="cursor-pointer">caveats ({l.caveats.length})</summary>
                          <ul className="list-disc ml-4">{l.caveats.map(c => <li key={c}>{c}</li>)}</ul>
                        </details>
                      )}
                    </td>
                    <td className={`${TD} whitespace-nowrap`}>
                      <button onClick={() => setHistory({ scope: l.scope, value: l.scopeValue, title: title(l) })}
                        className="p-1.5 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" title="History"><History className="w-4 h-4" /></button>
                      <button onClick={() => setPushing(l)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-gray-700 rounded"
                        title="Push to switch" aria-label={`Push ${title(l)} to switch`}><Send className="w-4 h-4" /></button>
                      {l.scope === 'PROVIDER' && /^\d+$/.test(l.scopeValue) && (
                        <button onClick={() => setTrunk(l)} className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 rounded"
                          title="Increase / decrease channels or CPS"><SlidersHorizontal className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-amber-700 dark:text-amber-400">Trunk changes made here: {NOT_YET_ENFORCED_COPY}</p>

          <SwitchRuntimeSection switches={panel.switches} />

          <div>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Switch rows cpass does not know about</h3>
            {panel.unmanaged.length === 0 ? <p className="text-xs text-gray-500">None.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50"><tr>{['Switch', 'Key', 'CPS / ch', 'Origin', 'In use', 'Notes'].map(h => <th key={h} className={TH}>{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {panel.unmanaged.map(r => (
                      <tr key={`${r.switchProviderId}-${r.origin}-${r.type}-${r.value}`}>
                        <td className={TD}>{r.switchName}</td>
                        <td className={`${TD} font-mono text-xs`}>{r.type}={r.value}</td>
                        <td className={TD}>{inf(r.cps)} / {inf(r.concurrency)}</td>
                        <td className={TD}>{r.origin === 'SEED' ? 'code seed' : 'DB row'}{r.enabled ? '' : ' (disabled)'}</td>
                        <td className={TD}>{r.effective ? 'yes' : 'no'}</td>
                        <td className={`${TD} text-xs`}>{r.notes || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Caller lookup per DID</h3>
            {panel.lookup.length === 0 ? <p className="text-xs text-gray-500">No lookup sources configured.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50"><tr>{['DID', 'Vendor', 'On switch', 'Status', 'Note'].map(h => <th key={h} className={TH}>{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {panel.lookup.map(r => (
                      <tr key={`${r.did}-${r.vendor}-${r.organizationId}`}>
                        <td className={`${TD} font-mono`}>{r.did}</td>
                        <td className={TD}>{r.vendor}</td>
                        <td className={TD}>{r.onSwitches.join(', ') || '—'}</td>
                        <td className={TD}>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${r.status === 'ENABLED_UNCONFIRMED' ? 'bg-amber-100 text-amber-800' : r.status === 'DISABLED' ? 'bg-gray-200 text-gray-600' : 'bg-red-100 text-red-800'}`}>
                            {r.status === 'ENABLED_UNCONFIRMED' ? 'Enabled (switch flag unconfirmed)' : r.status.replace(/_/g, ' ').toLowerCase()}
                          </span>
                        </td>
                        <td className={`${TD} text-xs`}>{r.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {history && <CapacityHistoryModal scope={history.scope} value={history.value} title={history.title} onClose={() => setHistory(null)} />}
      {pushing && (
        <PushToSwitchModal limitId={pushing.limitId} title={title(pushing)} onClose={() => setPushing(null)} onPushed={load} />
      )}
      {trunk && (
        <TrunkCapacityModal providerId={Number(trunk.scopeValue)} name={title(trunk)} cps={trunk.cps} maxChannels={trunk.maxChannels}
          onClose={() => setTrunk(null)}
          onSaved={() => { setTrunk(null); setNote(`Change recorded. ${NOT_YET_ENFORCED_COPY}`); load(); onChanged?.(); }} />
      )}
    </div>
  );
};

export default CapacityEnforcementPanel;
